import config from '../config';
import { auth } from './auth';

// Function to handle unauthorized responses
const handleUnauthorized = () => {
  auth.logout();
  // Dispatch a custom event that the app can listen to
  window.dispatchEvent(new CustomEvent('auth:unauthorized', {
    detail: { message: 'Your session has expired. Please log in again.' }
  }));
};

// Function to check if response is unauthorized
const isUnauthorized = (response, data) => {
  return response.status === 401 || 
         (response.status === 200 && data?.success === false && data?.message === 'Invalid token');
};

// Function to handle API responses
const handleResponse = async (response) => {
  const data = await response.json();
  if (isUnauthorized(response, data)) {
    handleUnauthorized();
    throw new Error('Session expired');
  }
  if (!response.ok) {
    // Create error object that preserves backend error details
    const error = new Error(data?.error || 'API request failed');
    error.statusCode = response.status;
    // Preserve the full backend payload so callers can inspect
    // structured fields (missing_serials, next_action, …). Older
    // callers still read error.results; keep the shim.
    error.data = data;
    if (data?.results) {
      error.results = data.results;
    }
    // Mark 503 errors as non-retryable
    if (response.status === 503) {
      error.noRetry = true;
    }
    throw error;
  }
  return data;
};

export const api = {
  async getDevices(options = {}) {
    try {
      const params = new URLSearchParams();
      if (options.compatible) {
        const compatList = Array.isArray(options.compatible)
          ? options.compatible
          : [options.compatible];
        const normalizedCompat = [...new Set(compatList
          .map((value) => value && value.toString().trim())
          .filter(Boolean))];
        if (normalizedCompat.length > 0) {
          params.append('compatible', normalizedCompat.join(','));
        }
      }

      if (Array.isArray(options.fields) && options.fields.length > 0) {
        const normalizedFields = [...new Set(options.fields.map(field => field?.toString().trim().toLowerCase()).filter(Boolean))];
        if (normalizedFields.length > 0) {
          params.append('fields', normalizedFields.join(','));
        }
      }

      const query = params.toString();
      const url = query ? `${config.apiUrl}/devices?${query}` : `${config.apiUrl}/devices`;

      // Use AbortController to set a 60-second timeout for initial device load
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const data = await handleResponse(response);
      
      // Check if backend explicitly says "no devices for project"
      const hasNoDevicesMessage = data?.message && data.message.includes('No devices connected');
      
      // Normalize { devices: null } to [] and prefer the devices array when present
      let devicesArray;
      if (Object.prototype.hasOwnProperty.call(data, 'devices')) {
        devicesArray = Array.isArray(data.devices) ? data.devices : [];
      } else {
        devicesArray = Array.isArray(data) ? data : [];
      }
      
      // Add metadata to response
      return {
        devices: devicesArray,
        noDevicesForProject: hasNoDevicesMessage,
        message: data?.message
      };
    } catch (error) {
      console.error('Error fetching devices:', error);
      if (error.message === 'Session expired') {
        // Re-throw to let the component handle it
        throw error;
      }
      // Handle timeout/abort errors with a clearer message
      if (error.name === 'AbortError') {
        const timeoutError = new Error('Request timed out while loading devices. Please try again.');
        timeoutError.isTimeout = true;
        throw timeoutError;
      }
      // Handle network-level fetch failures (proxy timeout, connection reset, etc.)
      if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
        const networkError = new Error('Network error while loading devices. The server may be initializing - please wait a moment and try again.');
        networkError.isNetworkError = true;
        throw networkError;
      }
      // For other errors, we might want to handle differently
      throw error;
    }
  },

  async getDevice(serial) {
    try {
      const response = await fetch(`${config.apiUrl}/${serial}/device`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching device details:', error);
      throw error;
    }
  },

  async getDeviceConfig(serial) {
    try {
      const response = await fetch(`${config.apiUrl}/${serial}/configure`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching device configuration:', error);
      throw error;
    }
  },

  async queryDeviceConfigBatch(serialNumbers) {
    const response = await fetch(`${config.apiUrl}/devices/configure/batch/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.getToken()}`
      },
      body: JSON.stringify({ serial_numbers: serialNumbers }),
    });
    return handleResponse(response);
  },

  async applyDeviceConfigBatch({ serialNumbers, configuration, updates } = {}) {
    const body = Array.isArray(updates)
      ? { updates }
      : { serial_numbers: serialNumbers, configuration };
    const response = await fetch(`${config.apiUrl}/devices/configure/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.getToken()}`
      },
      body: JSON.stringify(body),
    });
    return handleResponse(response);
  },

  mapBatchOperationResults(response) {
    return (response?.results || []).map((row) => ({
      serial: row.serial,
      success: !!row.success,
      message: row.message || row.error || '',
    }));
  },

  async queryAccessListBatch(serialNumbers) {
    const response = await fetch(`${config.apiUrl}/tree-mgmt/access-list/batch/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.getToken()}`
      },
      body: JSON.stringify({ serial_numbers: serialNumbers }),
    });
    return handleResponse(response);
  },

  async applyAccessListBatch({ serialNumbers, whitelist, blacklist, insistTimeSec, updates } = {}) {
    const body = Array.isArray(updates)
      ? { updates }
      : {
        serial_numbers: serialNumbers,
        whitelist: Array.isArray(whitelist) ? whitelist.join(',') : whitelist,
        blacklist: Array.isArray(blacklist) ? blacklist.join(',') : blacklist,
      };
    if (insistTimeSec !== undefined && insistTimeSec !== null && insistTimeSec !== '') {
      body.insist_time_sec = Number(insistTimeSec);
    }
    const response = await fetch(`${config.apiUrl}/tree-mgmt/access-list/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.getToken()}`
      },
      body: JSON.stringify(body),
    });
    return handleResponse(response);
  },

  async applySyslogConfigBatch(serialNumbers, syslogServer, { clear = false } = {}) {
    const response = await fetch(`${config.apiUrl}/devices/syslog-config/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.getToken()}`
      },
      body: JSON.stringify({
        serial_numbers: serialNumbers,
        syslog_server: syslogServer,
        clear,
      }),
    });
    return handleResponse(response);
  },

  async sendCommandBatch(serialNumbers, command) {
    const response = await fetch(`${config.apiUrl}/devices/command/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.getToken()}`
      },
      body: JSON.stringify({
        serial_numbers: serialNumbers,
        command,
      }),
    });
    return handleResponse(response);
  },

  async setStaBandBatch(serialNumbers, band) {
    const response = await fetch(`${config.apiUrl}/tree-mgmt/set-sta-band/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.getToken()}`
      },
      body: JSON.stringify({
        serial_numbers: serialNumbers,
        band,
      }),
    });
    return handleResponse(response);
  },

  async configureDevice(serial, configuration) {
    try {
      const response = await fetch(`${config.apiUrl}/${serial}/configure`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({
          serial_number: serial,
          uuid: Date.now().toString(),
          configuration: configuration,
          when: Date.now()
        }),
      });
      const data = await response.json();
      
      // Return the response data including any error message
      return data;
    } catch (error) {
      console.error('Error configuring device:', error);
      throw error;
    }
  },

  // Field-based Tree Management config (preserves Public WiFi sections).
  async getTreeMgmtConfig(serial) {
    const response = await fetch(`${config.apiUrl}/tree-mgmt/${serial}/config`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.getToken()}`
      }
    });
    return await handleResponse(response);
  },

  async patchTreeMgmtConfig(serial, patch) {
    const response = await fetch(`${config.apiUrl}/tree-mgmt/${serial}/config`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.getToken()}`
      },
      body: JSON.stringify(patch)
    });
    return await handleResponse(response);
  },

  async bulkPatchTreeMgmtConfig(devices, patch) {
    const response = await fetch(`${config.apiUrl}/tree-mgmt/devices/config`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.getToken()}`
      },
      body: JSON.stringify({ devices, patch })
    });
    return await handleResponse(response);
  },

  // Append a new treemgmt[] entry. The backend caps total entries at the
  // number of radio[] entries on the device (e.g. 2 on a Node).
  async addTreeMgmtEntry(serial, entry) {
    const response = await fetch(`${config.apiUrl}/tree-mgmt/${serial}/trees`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.getToken()}`
      },
      body: JSON.stringify(entry)
    });
    return await handleResponse(response);
  },

  async startRttySession(serial) {
    try {
      const response = await fetch(`${config.apiUrl}/${serial}/rtty`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({})
      });
      const data = await handleResponse(response);

      return data;
    } catch (error) {
      console.error('Error starting RTTY session:', error);
      throw error;
    }
  },

  async deleteDevice(serial) {
    try {
      const response = await fetch(`${config.apiUrl}/${serial}/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({
          serial_number: serial
        })
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error deleting device:', error);
      throw error;
    }
  },

  async getNeighborsReport(serial, golden = false) {
    try {
      const base = serial
        ? `${config.apiUrl}/tree-mgmt/${serial}/neighbors`
        : `${config.apiUrl}/tree-mgmt/neighbors`;
      const url = golden ? `${base}?golden=true` : base;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      const data = await handleResponse(response);

      return data;
    } catch (error) {
      console.error('Error getting neighbors report:', error);
      throw error;
    }
  },

  async getAllNeighborsReports(golden = false) {
    return this.getNeighborsReport(null, golden);
  },

  async getHeardBy(serial, minStaRssiDbm = -75) {
    try {
      const params = new URLSearchParams({
        min_sta_rssi: String(minStaRssiDbm),
      });
      const response = await fetch(
        `${config.apiUrl}/tree-mgmt/${encodeURIComponent(serial)}/heard-by?${params}`,
        {
          headers: {
            Authorization: `Bearer ${auth.getToken()}`,
          },
        },
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Error getting heard-by report:', error);
      throw error;
    }
  },

  async getTmReport(serial) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/${serial}/report`, {
        headers: {
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error getting TM report:', error);
      throw error;
    }
  },

  async getTreeMgmtInfo(serial, { dualCap = false, treeId = '' } = {}) {
    try {
      const params = new URLSearchParams();
      if (dualCap) {
        params.append('dual_cap', 'true');
      }
      if (treeId) {
        params.append('tree_id', treeId);
      }
      const query = params.toString();
      const url = query
        ? `${config.apiUrl}/tree-mgmt/${serial}/tm-info?${query}`
        : `${config.apiUrl}/tree-mgmt/${serial}/tm-info`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching tree management info:', error);
      throw error;
    }
  },

  async setWhitelist(serial, whitelist) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/${serial}/white-list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({
          serial_number: serial,
          whitelist: whitelist
        }),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error setting whitelist:', error);
      throw error;
    }
  },

  async setBlacklist(serial, blacklist) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/${serial}/black-list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({
          serial_number: serial,
          blacklist: blacklist
        }),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error setting blacklist:', error);
      throw error;
    }
  },

  async getAccessList(serial) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/${serial}/access-list`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error getting access list:', error);
      throw error;
    }
  },

  async setAccessList(serial, whitelist = [], blacklist = [], insistTimeSec = undefined) {
    try {
      const body = {
        serial_number: serial,
        whitelist: Array.isArray(whitelist) ? whitelist.join(',') : whitelist,
        blacklist: Array.isArray(blacklist) ? blacklist.join(',') : blacklist,
      };
      if (insistTimeSec !== undefined && insistTimeSec !== null && insistTimeSec !== '') {
        body.insist_time_sec = Number(insistTimeSec);
      }
      const response = await fetch(`${config.apiUrl}/tree-mgmt/${serial}/access-list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify(body),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error setting access list:', error);
      throw error;
    }
  },

  async setWhitelistInsistTime(serial, insistTimeSec) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/${serial}/whitelist-insist-time`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({
          serial_number: serial,
          insist_time_sec: insistTimeSec
        }),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error setting whitelist insist time:', error);
      throw error;
    }
  },

  async setParent(serial, parentSerial) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/${serial}/connect-to-ap`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({
          serial_number: serial,
          parent_serial: parentSerial
        }),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error setting parent:', error);
      throw error;
    }
  },

  async setTreeApChannel(serial, channel, band) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/${serial}/set-ap-channel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({
          serial_number: serial,
          channel: channel,
          band: band
        }),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error setting tree AP channel:', error);
      throw error;
    }
  },

  /**
   * POST /tree-mgmt/{serial}/set-sta-band
   * @param {string} serial
   * @param {{ band: string }} body band: "2"|"3"|"5GHz"|"6GHz"
   */
  async setStaBand(serial, body) {
    const band = typeof body === 'string' ? body : body?.band;
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/${serial}/set-sta-band`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`,
        },
        body: JSON.stringify({ band }),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error setting STA band:', error);
      throw error;
    }
  },

  async sendCommand(serial, command) {
    try {
      const response = await fetch(`${config.apiUrl}/${serial}/command`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({
          serial_number: serial,
          command: command,
        }),
      });
      const data = await handleResponse(response);
      if (!response.ok) {
        throw new Error(data.message || 'Failed to send command');
      }
      return {
        success: true,
        command_id: data.command_id,
        uuid: data.uuid
      };
    } catch (error) {
      console.error('Error sending command:', error);
      throw error;
    }
  },

  async getDeviceReport(serial) {
    try {
      const response = await fetch(`${config.apiUrl}/${serial}/report`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      if (!response.ok) {
        throw new Error('Failed to get device report');
      }
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching device report:', error);
      throw error;
    }
  },

  async getDefaultConfig(model) {
    const response = await fetch(`${config.apiUrl}/default-config/${model}?compatible=jnet2`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${auth.getToken()}`,
        'Content-Type': 'application/json',
      },
    });
    return handleResponse(response);
  },

  async setDefaultConfig(data) {
    const response = await fetch(`${config.apiUrl}/default-config`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${auth.getToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async restoreDefaultConfig(payload) {
    const response = await fetch(`${config.apiUrl}/default-config/restore`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${auth.getToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  async storeLastKnownGood(serial) {
    const response = await fetch(`${config.apiUrl}/${serial}/last-known-good`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${auth.getToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ serial_number: serial }),
    });
    return handleResponse(response);
  },

  async storeLastKnownGoodBatch(serialNumbers) {
    const response = await fetch(`${config.apiUrl}/devices/last-known-good/batch`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${auth.getToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ serial_numbers: serialNumbers }),
    });
    return handleResponse(response);
  },

  async getLastKnownGoodStatus(serial, { includeConfig = false } = {}) {
    const qs = includeConfig ? '?include=config' : '';
    const response = await fetch(`${config.apiUrl}/${serial}/last-known-good${qs}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${auth.getToken()}`,
        'Content-Type': 'application/json',
      },
    });
    return handleResponse(response);
  },

  async getAllLastKnownGoodStatus() {
    const response = await fetch(`${config.apiUrl}/devices/last-known-good`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${auth.getToken()}`,
        'Content-Type': 'application/json',
      },
    });
    return handleResponse(response);
  },

  async restoreLastKnownGood(serial) {
    const response = await fetch(`${config.apiUrl}/${serial}/last-known-good/restore`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${auth.getToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ serial_number: serial }),
    });
    return handleResponse(response);
  },

  async restoreLastKnownGoodBatch(serialNumbers) {
    const response = await fetch(`${config.apiUrl}/devices/last-known-good/batch/restore`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${auth.getToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ serial_numbers: serialNumbers }),
    });
    return handleResponse(response);
  },

  async storeAllLastKnownGood() {
    const response = await fetch(`${config.apiUrl}/devices/last-known-good`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${auth.getToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });
    return handleResponse(response);
  },

  async restoreAllLastKnownGood() {
    const response = await fetch(`${config.apiUrl}/devices/last-known-good/restore`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${auth.getToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });
    return handleResponse(response);
  },

  async getTreeProposal(options = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (options.rssi_threshold !== undefined) {
        queryParams.append('rssi_threshold', options.rssi_threshold);
      }
      
      if (options.max_children_per_parent !== undefined) {
        queryParams.append('max_children_per_parent', options.max_children_per_parent);
      }
      
      const queryString = queryParams.toString();
      const url = `${config.apiUrl}/tree-proposal${queryString ? `?${queryString}` : ''}`;
      

      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify(options)
      });
      
      return await handleResponse(response);
    } catch (error) {
      console.error('Error getting tree proposal:', error);
      throw error;
    }
  },

  async applyTreeProposal(data) {
    try {

      
      const response = await fetch(`${config.apiUrl}/apply-tree-proposal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify(data)
      });
      
      const result = await handleResponse(response);
      return result;
    } catch (error) {
      console.error('Error applying tree proposal:', error);
      throw error;
    }
  },

  async updateDevicesConnectivity(payload) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/devices/tm-params-update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify(payload)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error updating connectivity:', error);
      throw error;
    }
  },

  async getCustomTrees() {
    try {

      const response = await fetch(`${config.apiUrl}/tree-mgmt/custom-trees`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      const data = await handleResponse(response);
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to fetch custom trees');
      }
      return data;
    } catch (error) {
      console.error('Error fetching custom trees:', error);
      throw error;
    }
  },

  async createCustomTree(payload) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/custom-trees`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify(payload)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error creating custom tree:', error);
      throw error;
    }
  },

  async addDevicesToCustomTree(customTreeId, payload) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/custom-trees/${customTreeId}/devices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify(payload)
      });
      const data = await handleResponse(response);
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to add devices to custom tree');
      }
      return data;
    } catch (error) {
      console.error('Error adding devices to custom tree:', error);
      throw error;
    }
  },

  async removeDevicesFromCustomTree(customTreeId, payload) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/custom-trees/${customTreeId}/devices/remove`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify(payload)
      });
      const data = await handleResponse(response);
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to remove devices from custom tree');
      }
      return data;
    } catch (error) {
      console.error('Error removing devices from custom tree:', error);
      throw error;
    }
  },

  async deleteCustomTree(customTreeId) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/custom-trees/${customTreeId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error deleting custom tree:', error);
      throw error;
    }
  },

  // ---------------------------------------------------------------
  // Tree snapshots
  // ---------------------------------------------------------------
  // The snapshot module persists the actuator-controllable mesh
  // state (per-device parent + STA whitelist + per-radio
  // channel/width/tx-power) so the operator can save the network
  // after analysis-apply and later compare or re-apply it. The
  // diff endpoint returns ready-to-fire actuator actions in the
  // exact same shape the analyser endpoints emit, so the existing
  // per-action UI runner can reuse them unchanged.

  async createTreeSnapshot(payload) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/snapshots`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify(payload || {})
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error creating tree snapshot:', error);
      throw error;
    }
  },

  async listTreeSnapshots() {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/snapshots`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      const data = await handleResponse(response);
      return data?.snapshots || [];
    } catch (error) {
      console.error('Error listing tree snapshots:', error);
      throw error;
    }
  },

  async getTreeSnapshot(snapshotId) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/snapshots/${snapshotId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error getting tree snapshot:', error);
      throw error;
    }
  },

  async deleteTreeSnapshot(snapshotId) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/snapshots/${snapshotId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      // 204 has no body — handleResponse expects JSON. Short-circuit.
      if (response.status === 204) return { success: true };
      return await handleResponse(response);
    } catch (error) {
      console.error('Error deleting tree snapshot:', error);
      throw error;
    }
  },

  async diffTreeSnapshot(snapshotId) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/snapshots/${snapshotId}/diff`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error computing tree snapshot diff:', error);
      throw error;
    }
  },

  // ---------------------------------------------------------------
  // Plan runs
  // ---------------------------------------------------------------
  // A plan run wraps a set of analyser/snapshot actions as a single
  // monitored unit: the backend dispatches each action, captures the
  // pre-apply device state, then watches subsequent device state
  // reports to verify the change actually landed. The UI polls
  // getPlanRun every few seconds while a run is in `running` to
  // render plan-vs-actual progress.
  //
  // createPlanRun returns 409 Conflict when any of the targeted
  // serials already has an open plan — the caller should surface the
  // returned plan_id so the operator can jump to the live run.

  async createPlanRun(payload) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/plans`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify(payload || {})
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error creating plan run:', error);
      throw error;
    }
  },

  async listPlanRuns() {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/plans`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      const data = await handleResponse(response);
      return data?.plans || [];
    } catch (error) {
      console.error('Error listing plan runs:', error);
      throw error;
    }
  },

  async getPlanRun(planId) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/plans/${planId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error getting plan run:', error);
      throw error;
    }
  },

  // Delete a completed plan-run from history. The backend rejects
  // any attempt to delete a `running` plan with 409 — the operator
  // must cancel it first. Returns true on a successful delete,
  // throws otherwise; the thrown error preserves status and the
  // backend's structured body (status, plan_id) on 409 so the
  // caller can render a tailored snackbar.
  //
  // The backend returns 200 with `{deleted, id}` (we deliberately
  // avoid 204 because Apigee rejects any 204 that carries a body
  // with `protocol.http.ResponseWithBody`). 204 is still accepted
  // here for forward/backward compatibility with older deployments.
  async deletePlanRun(planId) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/plans/${planId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      if (response.status === 204) {
        return true;
      }
      // Parse JSON only if there's something to parse; both the
      // success and failure paths land here for non-204 responses.
      let data = null;
      try { data = await response.json(); } catch (_) { /* empty body */ }
      if (isUnauthorized(response, data)) {
        handleUnauthorized();
        throw new Error('Session expired');
      }
      if (response.ok) {
        return true;
      }
      const error = new Error(data?.error || 'Failed to delete plan run');
      error.statusCode = response.status;
      error.data = data;
      throw error;
    } catch (error) {
      console.error('Error deleting plan run:', error);
      throw error;
    }
  },

  // ---------------------------------------------------------------
  // Plan run lifecycle (Phase 2)
  // ---------------------------------------------------------------
  // Cancel an in-flight plan. The reason is optional and surfaces
  // on every action that gets flipped to "cancelled". 200 returns
  // the post-cancel doc; already-terminal plans 200 unchanged.
  async cancelPlanRun(planId, reason) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/plans/${planId}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify(reason ? { reason } : {})
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error cancelling plan run:', error);
      throw error;
    }
  },

  // Retry the failed/timed_out/cancelled actions of a previous plan
  // run. Pass `actionIds` to narrow the retry; omit to retry every
  // non-verified action. Returns `{ plan, source_plan_id, retried_actions }`.
  async retryPlanRun(planId, opts = {}) {
    try {
      const body = {};
      if (Array.isArray(opts.actionIds) && opts.actionIds.length > 0) {
        body.action_ids = opts.actionIds;
      }
      if (opts.name) {
        body.name = opts.name;
      }
      const response = await fetch(`${config.apiUrl}/tree-mgmt/plans/${planId}/retry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify(body)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error retrying plan run:', error);
      throw error;
    }
  },

  // Open a Server-Sent Events subscription on a plan run.
  //
  // We deliberately use fetch() + ReadableStream rather than the
  // browser EventSource because EventSource cannot send custom
  // headers — it would force us to leak the JWT into the URL
  // (?token=...) where it shows up in proxy access logs and
  // browser history. fetch() lets us send the standard
  // `Authorization: Bearer <jwt>` header just like every other API
  // call, so Apigee resolves the project id from the JWT exactly
  // the way it does for /devices, /tree-mgmt/*, and so on.
  //
  // Callbacks:
  //   onSnapshot(plan)  fired for every "snapshot" SSE event
  //   onTerminal(plan)  fired once on the "terminal" event (run is done)
  //   onError(err)      fired if the connection drops or fails to open
  //
  // Returns a `close()` function — always call it from a useEffect
  // cleanup so the underlying request gets aborted and the body
  // reader released when the component unmounts.
  subscribePlanRun(planId, { onSnapshot, onTerminal, onError } = {}) {
    if (!planId || typeof window === 'undefined' || typeof window.fetch !== 'function') {
      if (typeof onError === 'function') {
        onError(new Error('Streaming fetch not available in this environment'));
      }
      return () => {};
    }
    const url = `${config.apiUrl}/tree-mgmt/plans/${planId}/stream`;
    const controller = new AbortController();

    const safeParse = (raw) => {
      try {
        return JSON.parse(raw);
      } catch (e) {
        return null;
      }
    };

    // Dispatch one parsed SSE event to the right callback. Per the
    // SSE spec, multiple data: lines on the same event are joined
    // with newlines.
    const dispatchEvent = (eventName, dataLines) => {
      if (!eventName || dataLines.length === 0) return;
      const data = safeParse(dataLines.join('\n'));
      if (!data) return;
      if (eventName === 'snapshot' && typeof onSnapshot === 'function') {
        onSnapshot(data);
      } else if (eventName === 'terminal' && typeof onTerminal === 'function') {
        onTerminal(data);
      }
    };

    const consume = async () => {
      let response;
      try {
        response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache',
            'Authorization': `Bearer ${auth.getToken()}`,
          },
          signal: controller.signal,
          cache: 'no-store',
        });
      } catch (err) {
        if (controller.signal.aborted) return;
        if (typeof onError === 'function') onError(err);
        return;
      }

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        const err = new Error(`SSE handshake failed: ${response.status}${text ? ' ' + text : ''}`);
        err.status = response.status;
        if (typeof onError === 'function') onError(err);
        return;
      }
      if (!response.body || typeof response.body.getReader !== 'function') {
        if (typeof onError === 'function') {
          onError(new Error('SSE streaming body not supported by this browser'));
        }
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let eventName = '';
      let dataLines = [];

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // SSE frames are line-delimited. Each frame ends on a
          // blank line; lines are CRLF or LF.
          let nl;
          while ((nl = buffer.indexOf('\n')) !== -1) {
            let raw = buffer.slice(0, nl);
            buffer = buffer.slice(nl + 1);
            if (raw.endsWith('\r')) raw = raw.slice(0, -1);

            if (raw === '') {
              dispatchEvent(eventName, dataLines);
              eventName = '';
              dataLines = [];
              continue;
            }
            // Comment / heartbeat (": ping" from the backend).
            if (raw.startsWith(':')) continue;

            const colon = raw.indexOf(':');
            const field = colon === -1 ? raw : raw.slice(0, colon);
            let val = colon === -1 ? '' : raw.slice(colon + 1);
            if (val.startsWith(' ')) val = val.slice(1);

            if (field === 'event') {
              eventName = val;
            } else if (field === 'data') {
              dataLines.push(val);
            }
            // id / retry fields are ignored — we don't reconnect
            // automatically; the caller falls back to polling on
            // error.
          }
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        if (typeof onError === 'function') onError(err);
        return;
      }

      // Server closed the stream cleanly without a terminal event
      // (e.g. proxy timeout). Surface as an error so the caller can
      // swap to polling rather than silently freezing.
      if (!controller.signal.aborted && typeof onError === 'function') {
        onError(new Error('SSE stream closed unexpectedly'));
      }
    };

    consume();

    return () => {
      try { controller.abort(); } catch (e) { /* ignore */ }
    };
  },

  async startNeighborsScanAll(chanScanInterval = 5, allFreq = true) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/neighbors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({
          chan_scan_interval: chanScanInterval,
          all_freq: allFreq
        })
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Error starting neighbors scan for all devices:', error);
      throw error;
    }
  },

  async startNeighborsScanBatch(serialNumbers, chanScanInterval = 5, allFreq = true) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/neighbors/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({
          serial_numbers: serialNumbers,
          chan_scan_interval: chanScanInterval,
          all_freq: allFreq
        })
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Error starting batch neighbors scan:', error);
      throw error;
    }
  },

  async startNeighborsScanDevice(serialNumber, chanScanInterval = 5, allFreq = true) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/${serialNumber}/neighbors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({
          serial_number: serialNumber,
          chan_scan_interval: chanScanInterval,
          all_freq: allFreq
        })
      });

      return await handleResponse(response);
    } catch (error) {
      console.error(`Error starting neighbors scan for device ${serialNumber}:`, error);
      throw error;
    }
  },

  async startFileDownload(serial, data) {
    try {
      const response = await fetch(`${config.apiUrl}/${serial}/rtty/file/download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify(data)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error starting file download:', error);
      throw error;
    }
  },

  async startFileUpload(serial, data) {
    try {
      const response = await fetch(`${config.apiUrl}/${serial}/rtty/file/upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify(data)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error starting file upload:', error);
      throw error;
    }
  },

  async sendFileChunk(serial, data) {
    try {
      const response = await fetch(`${config.apiUrl}/${serial}/rtty/file/chunk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify(data)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error sending file chunk:', error);
      throw error;
    }
  },

  async runIperf(serial, params) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/${serial}/iperf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify(params)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error running iPerf:', error);
      throw error;
    }
  },

  // ---------------------------------------------------------------
  // Tree-wide iperf test runs
  // ---------------------------------------------------------------

  async createIperfTestRun(payload) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/iperf-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify(payload)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error creating iperf test run:', error);
      throw error;
    }
  },

  async listIperfTestRuns() {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/iperf-test`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error listing iperf test runs:', error);
      throw error;
    }
  },

  // listIperfTestRunsMatching pulls completed iperf runs that overlap
  // the current tree for the given optimize scope. Each row carries a
  // live `match_score` so the UI can sort and label entries without
  // recomputing. Scope params mirror the optimize request body.
  async listIperfTestRunsMatching({ customTreeId, customTreeIds, capSerials, focusSerials } = {}) {
    try {
      const url = new URL(`${config.apiUrl}/tree-mgmt/iperf-test/matching`);
      if (customTreeId) {
        url.searchParams.set('custom_tree_id', customTreeId);
      }
      (customTreeIds || []).forEach((treeId) => {
        const trimmed = String(treeId || '').trim();
        if (trimmed) url.searchParams.append('custom_tree_ids', trimmed);
      });
      (capSerials || []).forEach((cap) => {
        const trimmed = String(cap || '').trim();
        if (trimmed) url.searchParams.append('cap_serials', trimmed);
      });
      (focusSerials || []).forEach((serial) => {
        const trimmed = String(serial || '').trim();
        if (trimmed) url.searchParams.append('focus_serials', trimmed);
      });
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error listing matching iperf test runs:', error);
      throw error;
    }
  },

  async getIperfTestRun(id) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/iperf-test/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error getting iperf test run:', error);
      throw error;
    }
  },

  async cancelIperfTestRun(id) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/iperf-test/${id}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error cancelling iperf test run:', error);
      throw error;
    }
  },

  async retryIperfTestRun(id) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/iperf-test/${id}/retry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error retrying iperf test run:', error);
      throw error;
    }
  },

  async retryIperfSingleTest(runId, testId) {
    const enc = encodeURIComponent(testId);
    try {
      const response = await fetch(
        `${config.apiUrl}/tree-mgmt/iperf-test/${runId}/tests/${enc}/retry`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.getToken()}`
          }
        }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error retrying single iperf test:', error);
      throw error;
    }
  },

  async deleteIperfTestRun(id) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/iperf-test/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      if (response.status === 204) return true;
      let data = null;
      try { data = await response.json(); } catch (_) { /* empty */ }
      if (isUnauthorized(response, data)) {
        handleUnauthorized();
        throw new Error('Session expired');
      }
      if (response.ok) return true;
      const error = new Error(data?.error || 'Failed to delete iperf test run');
      error.statusCode = response.status;
      error.data = data;
      throw error;
    } catch (error) {
      console.error('Error deleting iperf test run:', error);
      throw error;
    }
  },

  subscribeIperfTestRun(id, { onSnapshot, onTerminal, onError } = {}) {
    if (!id || typeof window === 'undefined' || typeof window.fetch !== 'function') {
      if (typeof onError === 'function') {
        onError(new Error('Streaming fetch not available'));
      }
      return () => {};
    }
    const url = `${config.apiUrl}/tree-mgmt/iperf-test/${id}/stream`;
    const controller = new AbortController();

    const safeParse = (raw) => {
      try { return JSON.parse(raw); } catch (e) { return null; }
    };

    const dispatchEvent = (eventName, dataLines) => {
      if (!eventName || dataLines.length === 0) return;
      const data = safeParse(dataLines.join('\n'));
      if (!data) return;
      if (eventName === 'snapshot' && typeof onSnapshot === 'function') onSnapshot(data);
      else if (eventName === 'terminal' && typeof onTerminal === 'function') onTerminal(data);
    };

    const consume = async () => {
      let response;
      try {
        response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache',
            'Authorization': `Bearer ${auth.getToken()}`,
          },
          signal: controller.signal,
          cache: 'no-store',
        });
      } catch (err) {
        if (controller.signal.aborted) return;
        if (typeof onError === 'function') onError(err);
        return;
      }

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        const err = new Error(`SSE handshake failed: ${response.status}${text ? ' ' + text : ''}`);
        err.status = response.status;
        if (typeof onError === 'function') onError(err);
        return;
      }
      if (!response.body || typeof response.body.getReader !== 'function') {
        if (typeof onError === 'function') onError(new Error('SSE not supported'));
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let eventName = '';
      let dataLines = [];

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let nl;
          while ((nl = buffer.indexOf('\n')) !== -1) {
            let raw = buffer.slice(0, nl);
            buffer = buffer.slice(nl + 1);
            if (raw.endsWith('\r')) raw = raw.slice(0, -1);
            if (raw === '') {
              dispatchEvent(eventName, dataLines);
              eventName = '';
              dataLines = [];
              continue;
            }
            if (raw.startsWith(':')) continue;
            const colon = raw.indexOf(':');
            const field = colon === -1 ? raw : raw.slice(0, colon);
            let val = colon === -1 ? '' : raw.slice(colon + 1);
            if (val.startsWith(' ')) val = val.slice(1);
            if (field === 'event') eventName = val;
            else if (field === 'data') dataLines.push(val);
          }
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        if (typeof onError === 'function') onError(err);
        return;
      }
      if (!controller.signal.aborted && typeof onError === 'function') {
        onError(new Error('SSE stream closed unexpectedly'));
      }
    };

    consume();
    return () => { try { controller.abort(); } catch (_) { /* ignore */ } };
  },

  async getChannelAnalysis({ 
    channelGap,  // Deprecated, kept for backward compatibility
    channelBandwidth, 
    threshold,
    minFrequencySeparationMHz,
    neighborRssiFloorDbm,
    centerLat, 
    centerLng,
    capOnlyActions,
    capOnlyAnalysis
  } = {}) {
    try {
      const params = new URLSearchParams();
      // Use channelBandwidth if provided, otherwise fall back to channelGap
      if (channelBandwidth !== undefined) {
        params.append('channel_bandwidth', channelBandwidth);
      } else if (channelGap !== undefined) {
        params.append('channel_gap_mhz', channelGap);
      }
      if (threshold !== undefined) {
        params.append('threshold', threshold);
      }
      if (minFrequencySeparationMHz !== undefined) {
        params.append('min_frequency_separation_mhz', minFrequencySeparationMHz);
      }
      if (neighborRssiFloorDbm !== undefined) {
        params.append('neighbor_rssi_floor_dbm', neighborRssiFloorDbm);
      }
      if (centerLat !== undefined && centerLng !== undefined) {
        params.append('center_lat', centerLat);
        params.append('center_lng', centerLng);
      }
      if (capOnlyActions === true) {
        params.append('cap_only_actions', 'true');
      }
      if (capOnlyAnalysis === true) {
        params.append('cap_only_analysis', 'true');
      }
      const query = params.toString();
      const url = query
        ? `${config.apiUrl}/tree-mgmt/analysis/channel?${query}`
        : `${config.apiUrl}/tree-mgmt/analysis/channel`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching channel analysis:', error);
      throw error;
    }
  },

  async executeChannelAction(action) {
    if (!action || !action.endpoint) {
      throw new Error('Invalid action payload');
    }
    try {
      const method = (action.method || 'POST').toUpperCase();
      const url = action.endpoint.startsWith('http')
        ? action.endpoint
        : `${config.apiUrl}${action.endpoint}`;
      const options = {
        method,
        headers: {
          'Authorization': `Bearer ${auth.getToken()}`,
        },
      };
      const hasBody = method !== 'GET' && method !== 'HEAD';
      if (hasBody) {
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify(action.payload || {});
      }
      const response = await fetch(url, options);
      return await handleResponse(response);
    } catch (error) {
      console.error('Error executing channel analysis action:', error);
      throw error;
    }
  },

  // -------------------------------------------------------------------------
  // Tree-optimisation analysers — Phase 1 read-only endpoints + Phase 2 meta.
  // Each returns `summary`, `score` (canonical AnalysisScore block),
  // `issues[]` and `actions[]` (advisory analysers omit `actions`).
  // executeAnalyzerAction is the generic "POST/PATCH back to the endpoint
  // the action carries" runner — every analyser embeds a ready-to-fire
  // {endpoint, method, payload} so the UI can apply actions one-by-one
  // without analyser-specific glue.
  // -------------------------------------------------------------------------

  async _getJSON(path) {
    const response = await fetch(`${config.apiUrl}${path}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.getToken()}`,
      },
    });
    return await handleResponse(response);
  },

  async getHealthAnalysis() {
    try {
      return await this._getJSON('/tree-mgmt/analysis/health');
    } catch (error) {
      console.error('Error fetching health analysis:', error);
      throw error;
    }
  },

  async getTopologyAnalysis() {
    try {
      return await this._getJSON('/tree-mgmt/analysis/topology');
    } catch (error) {
      console.error('Error fetching topology analysis:', error);
      throw error;
    }
  },

  async getPowerAnalysis() {
    try {
      return await this._getJSON('/tree-mgmt/analysis/power');
    } catch (error) {
      console.error('Error fetching power analysis:', error);
      throw error;
    }
  },

  async getPowerAnalysisWithParams({ targetRssiDbm, includeLinks = true } = {}) {
    try {
      const params = new URLSearchParams();
      if (includeLinks) params.set('include_links', 'true');
      if (targetRssiDbm != null) params.set('target_rssi_dbm', String(targetRssiDbm));
      const qs = params.toString();
      return await this._getJSON(`/tree-mgmt/analysis/power${qs ? `?${qs}` : ''}`);
    } catch (error) {
      console.error('Error fetching power analysis with params:', error);
      throw error;
    }
  },

  async getBackupParentsAnalysis() {
    try {
      return await this._getJSON('/tree-mgmt/analysis/backup-parents');
    } catch (error) {
      console.error('Error fetching backup-parents analysis:', error);
      throw error;
    }
  },

  // -------------------------------------------------------------------------
  // Port forwarding (jnet2 network boards only).
  //
  // GET /tree-mgmt/{serial}/port-forwarding returns the device's current
  // port-forwarding rules (the port-forward subset of the `app[]` array
  // in the device config). PUT replaces the rule set atomically; other
  // app[] entries (firewall toggles, etc.) are preserved by the backend.
  // -------------------------------------------------------------------------

  async getPortForwarding(serial) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/${serial}/port-forwarding`, {
        headers: {
          'Authorization': `Bearer ${auth.getToken()}`,
        },
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching port-forwarding rules:', error);
      throw error;
    }
  },

  async putPortForwarding(serial, rules) {
    try {
      const response = await fetch(`${config.apiUrl}/tree-mgmt/${serial}/port-forwarding`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`,
        },
        body: JSON.stringify({ rules: rules || [] }),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error updating port-forwarding rules:', error);
      throw error;
    }
  },

  async getSyslogConfig(serial) {
    try {
      const response = await fetch(`${config.apiUrl}/${serial}/syslog-config`, {
        headers: {
          'Authorization': `Bearer ${auth.getToken()}`,
        },
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching syslog config:', error);
      throw error;
    }
  },

  async setSyslogConfig(serial, syslogServer) {
    try {
      const response = await fetch(`${config.apiUrl}/${serial}/syslog-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`,
        },
        body: JSON.stringify({ syslog_server: syslogServer }),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error setting syslog config:', error);
      throw error;
    }
  },

  // getAnalysisDataStatus returns the neighbour-data completeness +
  // freshness snapshot used as the pre-flight gate for the tree
  // optimisation analysers. The UI calls it on mount so the
  // freshness chip on each analyser panel renders before the
  // operator clicks Run.
  async getAnalysisDataStatus(golden = false) {
    try {
      const qs = golden ? '?golden=true' : '';
      return await this._getJSON(`/tree-mgmt/analysis/neighbors-data-status${qs}`);
    } catch (error) {
      console.error('Error fetching analysis data status:', error);
      throw error;
    }
  },

  async markNeighborsGolden(serial) {
    try {
      const url = serial
        ? `${config.apiUrl}/tree-mgmt/${serial}/neighbors/golden`
        : `${config.apiUrl}/tree-mgmt/neighbors/golden`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${auth.getToken()}`,
        },
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error marking neighbors as golden:', error);
      throw error;
    }
  },

  async getAllPublicWifi() {
    try {
      return await this._getJSON('/public-wifi');
    } catch (error) {
      console.error('Error fetching public wifi data:', error);
      throw error;
    }
  },

  // runPublicWifiRRMPlan is asynchronous on the wire (same pattern as
  // runOptimize) to dodge the GCLB hard 30 s request cap.
  runPublicWifiRRMPlan(coverageRadiusM = 30, options = {}) {
    const POLL_INTERVAL_MS = 2000;
    const MAX_POLL_DURATION_MS = 10 * 60 * 1000;
    const POLL_REQUEST_TIMEOUT_MS = 15000;

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    return (async () => {
      const params = new URLSearchParams({ coverage_radius_m: String(coverageRadiusM) });
      if (options.useIperfBackhaul !== undefined) {
        params.set('use_iperf_backhaul', options.useIperfBackhaul ? 'true' : 'false');
      }
      if (options.iperfDirection) {
        params.set('iperf_direction', options.iperfDirection);
      }
      if (options.requireIperf !== undefined) {
        params.set('require_iperf', options.requireIperf ? 'true' : 'false');
      }
      if (options.iperfRunId) {
        params.set('iperf_run_id', options.iperfRunId);
      }

      let kickoffResp;
      try {
        kickoffResp = await fetch(`${config.apiUrl}/public-wifi/rrm-plan?${params.toString()}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.getToken()}`,
          },
        });
      } catch (err) {
        console.error('Error starting public WiFi RRM plan job:', err);
        throw err;
      }

      if (!kickoffResp.ok) {
        return handleResponse(kickoffResp);
      }

      let kickoff;
      try {
        kickoff = await kickoffResp.json();
      } catch (err) {
        throw new Error('Public WiFi RRM kickoff returned an invalid body');
      }
      const jobId = kickoff && kickoff.job_id;
      if (!jobId) {
        throw new Error('Public WiFi RRM kickoff did not return a job_id');
      }

      const pollUrl = `${config.apiUrl}/public-wifi/rrm-plan/jobs/${encodeURIComponent(jobId)}`;
      const startedAt = Date.now();

      while (true) {
        if (Date.now() - startedAt > MAX_POLL_DURATION_MS) {
          throw new Error('Public WiFi RRM plan timed out (job did not finish within 10 minutes)');
        }
        await sleep(POLL_INTERVAL_MS);

        const ctrl = new AbortController();
        const tid = setTimeout(() => ctrl.abort(), POLL_REQUEST_TIMEOUT_MS);
        let resp;
        try {
          resp = await fetch(pollUrl, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${auth.getToken()}`,
            },
            signal: ctrl.signal,
          });
        } catch {
          clearTimeout(tid);
          continue;
        }
        clearTimeout(tid);

        if (resp.status === 404) {
          throw new Error('Public WiFi RRM result expired or was lost — please re-run');
        }
        if (!resp.ok) {
          continue;
        }

        let body;
        try {
          body = await resp.json();
        } catch {
          continue;
        }
        const status = body && body.status;
        if (status === 'completed') {
          if (!body.result) {
            throw new Error('Public WiFi RRM completed without a result payload');
          }
          return body.result;
        }
        if (status === 'failed') {
          const err = new Error(body.error || 'Public WiFi RRM job failed');
          err.statusCode = 500;
          err.data = body;
          throw err;
        }
      }
    })();
  },

  async getPublicWifiRRMPlan() {
    try {
      return await this._getJSON('/public-wifi/rrm-plan');
    } catch (error) {
      console.error('Error fetching public WiFi RRM plan:', error);
      throw error;
    }
  },

  /** GET /public-wifi/associated-clients — historical Public WiFi association rows (masked MACs). */
  async getPublicWifiAssociatedClientsHistory(fromIso, toIso, options = {}) {
    try {
      const params = new URLSearchParams();
      if (fromIso) params.append('from', fromIso);
      if (toIso) params.append('to', toIso);
      const serial = options.serial != null ? String(options.serial).trim() : '';
      if (serial) params.append('serial', serial);
      if (options.limit != null && Number(options.limit) > 0) {
        params.append('limit', String(Number(options.limit)));
      }
      const qs = params.toString();
      const path = qs ? `/public-wifi/associated-clients?${qs}` : '/public-wifi/associated-clients';
      return await this._getJSON(path);
    } catch (error) {
      console.error('Error fetching public WiFi associated clients history:', error);
      throw error;
    }
  },

  // runOptimize is asynchronous on the wire to dodge the hard 30 s
  // total-request cap that Google Cloud HTTPS LB enforces on the
  // path between the browser and Apigee.
  //
  //   1. POST /tree-mgmt/analysis/optimize  ->  202 { job_id, ... }
  //                  (sub-second; precondition errors still come
  //                   back as 4xx/5xx synchronously)
  //   2. GET  /tree-mgmt/analysis/optimize/jobs/{job_id}
  //                  polled every OPTIMIZE_POLL_INTERVAL_MS until
  //                  status is "completed" or "failed".
  //
  // The returned Promise resolves with the same envelope shape every
  // existing caller already expects ({...OptimizeResult, data_freshness}),
  // so no UI component needs to change.
  runOptimize(params = {}) {
    const POLL_INTERVAL_MS = 2000;
    // Hard ceiling so a runaway job never holds a UI Promise forever.
    // Backend optimize for large meshes finishes well under 5 minutes;
    // 10 minutes leaves ample headroom while still being a safety net.
    const MAX_POLL_DURATION_MS = 10 * 60 * 1000;
    // Per-poll request timeout. Each GET should answer in well under a
    // second; abort sooner so a wedged proxy does not stall the loop.
    const POLL_REQUEST_TIMEOUT_MS = 15000;

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    return (async () => {
      // ---- Step 1: kick off ---------------------------------------
      let kickoffResp;
      try {
        kickoffResp = await fetch(`${config.apiUrl}/tree-mgmt/analysis/optimize`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.getToken()}`,
          },
          body: JSON.stringify(params || {}),
        });
      } catch (err) {
        console.error('Error starting optimize job:', err);
        throw err;
      }

      if (!kickoffResp.ok) {
        // Reuse the standard error-shaping path so callers that
        // already inspect statusCode / data (e.g. 409
        // neighbor_data_incomplete in useAnalyserGate) keep working.
        return handleResponse(kickoffResp);
      }

      let kickoff;
      try {
        kickoff = await kickoffResp.json();
      } catch (err) {
        throw new Error('Optimize kickoff returned an invalid body');
      }
      const jobId = kickoff && kickoff.job_id;
      if (!jobId) {
        throw new Error('Optimize kickoff did not return a job_id');
      }

      // ---- Step 2: poll -------------------------------------------
      const pollUrl = `${config.apiUrl}/tree-mgmt/analysis/optimize/jobs/${encodeURIComponent(jobId)}`;
      const startedAt = Date.now();

      while (true) {
        if (Date.now() - startedAt > MAX_POLL_DURATION_MS) {
          throw new Error('Optimize timed out (job did not finish within 10 minutes)');
        }
        await sleep(POLL_INTERVAL_MS);

        const ctrl = new AbortController();
        const tid = setTimeout(() => ctrl.abort(), POLL_REQUEST_TIMEOUT_MS);
        let resp;
        try {
          resp = await fetch(pollUrl, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${auth.getToken()}`,
            },
            signal: ctrl.signal,
          });
        } catch (err) {
          // Transient network blip — keep polling unless we have
          // exhausted the overall budget. AbortError is treated the
          // same way (next loop iteration retries).
          clearTimeout(tid);
          continue;
        }
        clearTimeout(tid);

        if (resp.status === 404) {
          // Job evaporated server-side (reaped or backend restarted).
          // Surface as a clear error so the UI can prompt a re-run.
          throw new Error('Optimize result expired or was lost — please re-run');
        }
        if (!resp.ok) {
          // Treat any other 5xx the same as a transient blip; only
          // promote to a hard failure if it persists past the budget.
          continue;
        }

        let body;
        try {
          body = await resp.json();
        } catch {
          continue;
        }
        const status = body && body.status;
        if (status === 'completed') {
          if (!body.result) {
            throw new Error('Optimize completed without a result payload');
          }
          return body.result;
        }
        if (status === 'failed') {
          const errData = body.error_data || {};
          const errCode = body.error_code || '';
          const err = new Error(body.error || 'Optimize job failed');
          if (errCode === 'neighbor_data_incomplete') {
            err.statusCode = 409;
            err.data = errData;
          } else {
            err.statusCode = 500;
            err.data = body;
          }
          throw err;
        }
        // status === 'running' (or anything we do not recognise) →
        // keep polling.
      }
    })();
  },

  // executeAnalyzerAction handles every action shape returned by the
  // five analysers (set-parent, set-ap-channel, config PATCH, etc.)
  // by trusting the embedded {endpoint, method, payload} contract.
  async executeAnalyzerAction(action) {
    if (!action || !action.endpoint) {
      throw new Error('Invalid action payload');
    }
    try {
      const method = (action.method || 'POST').toUpperCase();
      const url = action.endpoint.startsWith('http')
        ? action.endpoint
        : `${config.apiUrl}${action.endpoint}`;
      const options = {
        method,
        headers: {
          'Authorization': `Bearer ${auth.getToken()}`,
        },
      };
      const hasBody = method !== 'GET' && method !== 'HEAD';
      if (hasBody) {
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify(action.payload || {});
      }
      const response = await fetch(url, options);
      return await handleResponse(response);
    } catch (error) {
      console.error('Error executing analyser action:', error);
      throw error;
    }
  },

  async updateDeviceGPS(serial, latitude, longitude, altitude = '') {
    try {
      const response = await fetch(`${config.apiUrl}/${serial}/gps`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        },
        body: JSON.stringify({
          latitude: latitude.toString(),
          longitude: longitude.toString(),
          altitude: altitude.toString()
        })
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error updating device GPS:', error);
      throw error;
    }
  },

  async patchDeviceMetadata(serial, { displayName, gpsOverride, clearGpsOverride } = {}) {
    const body = {};
    if (displayName !== undefined) {
      body.display_name = displayName;
    }
    if (gpsOverride) {
      body.gps_override = {
        lat: String(gpsOverride.lat),
        lng: String(gpsOverride.lng),
        ...(gpsOverride.alt != null && gpsOverride.alt !== '' ? { alt: String(gpsOverride.alt) } : {}),
      };
    }
    if (clearGpsOverride) {
      body.gps_override = null;
    }
    const response = await fetch(`${config.apiUrl}/${serial}/metadata`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.getToken()}`
      },
      body: JSON.stringify(body),
    });
    return handleResponse(response);
  },

  async getProjectNetworkStatus() {
    try {
      const response = await fetch(`${config.apiUrl}/project/network-status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching project network status:', error);
      throw error;
    }
  },

  async getProvisioningMismatch() {
    try {
      const response = await fetch(`${config.apiUrl}/provisioning/mismatch`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.getToken()}`
        }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching provisioning mismatch:', error);
      throw error;
    }
  },

  // --- Groups ---

  async getGroups(includeProfiles = false) {
    try {
      const url = `${config.apiUrl}/groups${includeProfiles ? '?include_profiles=true' : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching groups:', error);
      throw error;
    }
  },

  async getGroup(id, includeProfiles = true) {
    try {
      const url = `${config.apiUrl}/groups/${id}${includeProfiles ? '?include_profiles=true' : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching group:', error);
      throw error;
    }
  },

  async createGroup(data) {
    try {
      const response = await fetch(`${config.apiUrl}/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify(data)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error creating group:', error);
      throw error;
    }
  },

  async updateGroup(id, data) {
    try {
      const response = await fetch(`${config.apiUrl}/groups/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify(data)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error updating group:', error);
      throw error;
    }
  },

  // --- SSID Profiles ---

  async getSSIDProfiles() {
    try {
      const response = await fetch(`${config.apiUrl}/ssids`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching SSID profiles:', error);
      throw error;
    }
  },

  async createSSIDProfile(data) {
    try {
      const response = await fetch(`${config.apiUrl}/ssids`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify(data)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error creating SSID profile:', error);
      throw error;
    }
  },

  async updateSSIDProfile(id, data) {
    try {
      const response = await fetch(`${config.apiUrl}/ssids/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify(data)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error updating SSID profile:', error);
      throw error;
    }
  },

  async assignSSIDToGroup(ssidId, groupId, { deviceTreeIds } = {}) {
    try {
      const body = { group_id: groupId };
      if (deviceTreeIds && Object.keys(deviceTreeIds).length > 0) {
        body.device_tree_ids = deviceTreeIds;
      }
      const response = await fetch(`${config.apiUrl}/ssids/${ssidId}/assign-group`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify(body)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error assigning SSID to group:', error);
      throw error;
    }
  },

  async assignSSIDToDevice(ssidId, serial, { treeId } = {}) {
    try {
      const body = { serial };
      if (treeId) body.tree_id = treeId;
      const response = await fetch(`${config.apiUrl}/ssids/${ssidId}/assign-device`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify(body)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error assigning SSID to device:', error);
      throw error;
    }
  },

  async unassignSSIDFromDevice(ssidId, serial) {
    try {
      const response = await fetch(`${config.apiUrl}/ssids/${ssidId}/unassign-device`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify({ serial })
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error unassigning SSID from device:', error);
      throw error;
    }
  },

  // Bulk SSID assignment — one BE call for N serials/group_ids.
  //
  // The optional `signal` argument is a standard AbortSignal: pass
  // controller.signal and call controller.abort() to actually cancel the
  // in-flight bulk request when the user clicks Cancel in the dialog.
  // Without this, fetch keeps running after the modal closes and a late
  // snackbar can appear over an unrelated screen.
  async assignSSIDToDevices(ssidId, serials, { signal, deviceTreeIds } = {}) {
    try {
      const body = { serials };
      if (deviceTreeIds && Object.keys(deviceTreeIds).length > 0) {
        body.device_tree_ids = deviceTreeIds;
      }
      const response = await fetch(`${config.apiUrl}/ssids/${ssidId}/assign-devices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify(body),
        signal
      });
      return await handleResponse(response);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Error bulk-assigning SSID to devices:', error);
      }
      throw error;
    }
  },

  async unassignSSIDFromDevices(ssidId, serials, { signal } = {}) {
    try {
      const response = await fetch(`${config.apiUrl}/ssids/${ssidId}/unassign-devices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify({ serials }),
        signal
      });
      return await handleResponse(response);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Error bulk-unassigning SSID from devices:', error);
      }
      throw error;
    }
  },

  async assignSSIDToGroups(ssidId, groupIds, { signal, deviceTreeIds } = {}) {
    try {
      const body = { group_ids: groupIds };
      if (deviceTreeIds && Object.keys(deviceTreeIds).length > 0) {
        body.device_tree_ids = deviceTreeIds;
      }
      const response = await fetch(`${config.apiUrl}/ssids/${ssidId}/assign-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify(body),
        signal
      });
      return await handleResponse(response);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Error bulk-assigning SSID to groups:', error);
      }
      throw error;
    }
  },

  async unassignSSIDFromGroups(ssidId, groupIds, { signal } = {}) {
    try {
      const response = await fetch(`${config.apiUrl}/ssids/${ssidId}/unassign-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify({ group_ids: groupIds }),
        signal
      });
      return await handleResponse(response);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Error bulk-unassigning SSID from groups:', error);
      }
      throw error;
    }
  },

  // --- VLAN Profiles ---

  async getVLANProfiles() {
    try {
      const response = await fetch(`${config.apiUrl}/vlans`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching VLAN profiles:', error);
      throw error;
    }
  },

  async createVLANProfile(data) {
    try {
      const response = await fetch(`${config.apiUrl}/vlans`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify(data)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error creating VLAN profile:', error);
      throw error;
    }
  },

  async updateVLANProfile(id, data) {
    try {
      const response = await fetch(`${config.apiUrl}/vlans/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify(data)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error updating VLAN profile:', error);
      throw error;
    }
  },

  async unassignSSIDFromGroup(ssidId, groupId) {
    try {
      const response = await fetch(`${config.apiUrl}/ssids/${ssidId}/unassign-group`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify({ group_id: groupId })
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error unassigning SSID from group:', error);
      throw error;
    }
  },

  async assignVLANToSSID(ssidId, vlanId) {
    try {
      const response = await fetch(`${config.apiUrl}/ssids/${ssidId}/assign-vlan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify({ vlan_id: vlanId })
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error assigning VLAN to SSID:', error);
      throw error;
    }
  },

  async deleteGroup(id) {
    try {
      const response = await fetch(`${config.apiUrl}/groups/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${auth.getToken()}` }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error deleting group:', error);
      throw error;
    }
  },

  async deleteSSIDProfile(id) {
    try {
      const response = await fetch(`${config.apiUrl}/ssids/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${auth.getToken()}` }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error deleting SSID profile:', error);
      throw error;
    }
  },

  async deleteVLANProfile(id) {
    try {
      const response = await fetch(`${config.apiUrl}/vlans/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${auth.getToken()}` }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error deleting VLAN profile:', error);
      throw error;
    }
  },

  // --- RADIUS Profiles ---

  async getRadiusProfiles() {
    try {
      const response = await fetch(`${config.apiUrl}/radius-profiles`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching RADIUS profiles:', error);
      throw error;
    }
  },

  async createRadiusProfile(data) {
    try {
      const response = await fetch(`${config.apiUrl}/radius-profiles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify(data)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error creating RADIUS profile:', error);
      throw error;
    }
  },

  async updateRadiusProfile(id, data) {
    try {
      const response = await fetch(`${config.apiUrl}/radius-profiles/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify(data)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error updating RADIUS profile:', error);
      throw error;
    }
  },

  async deleteRadiusProfile(id) {
    try {
      const response = await fetch(`${config.apiUrl}/radius-profiles/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${auth.getToken()}` }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error deleting RADIUS profile:', error);
      throw error;
    }
  },

  async assignRadiusToSSID(ssidId, radiusProfileId) {
    try {
      const response = await fetch(`${config.apiUrl}/ssids/${ssidId}/assign-radius`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify({ radius_profile_id: radiusProfileId })
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error assigning RADIUS to SSID:', error);
      throw error;
    }
  },

  async unassignRadiusFromSSID(ssidId) {
    try {
      const response = await fetch(`${config.apiUrl}/ssids/${ssidId}/unassign-radius`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error unassigning RADIUS from SSID:', error);
      throw error;
    }
  },

  // --- Radio Profiles ---

  async getRadioProfiles() {
    try {
      const response = await fetch(`${config.apiUrl}/radio-profiles`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching radio profiles:', error);
      throw error;
    }
  },

  async createRadioProfile(data) {
    try {
      const response = await fetch(`${config.apiUrl}/radio-profiles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify(data)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error creating radio profile:', error);
      throw error;
    }
  },

  async updateRadioProfile(id, data) {
    try {
      const response = await fetch(`${config.apiUrl}/radio-profiles/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify(data)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error updating radio profile:', error);
      throw error;
    }
  },

  async patchRadioProfile(id, data) {
    try {
      const response = await fetch(`${config.apiUrl}/radio-profiles/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify(data)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error patching radio profile:', error);
      throw error;
    }
  },

  async deleteRadioProfile(id) {
    try {
      const response = await fetch(`${config.apiUrl}/radio-profiles/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${auth.getToken()}` }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error deleting radio profile:', error);
      throw error;
    }
  },

  async assignRadioProfileToGroup(profileId, groupId) {
    try {
      const response = await fetch(`${config.apiUrl}/radio-profiles/${profileId}/assign-group`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify({ group_id: groupId })
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error assigning radio profile to group:', error);
      throw error;
    }
  },

  async unassignRadioProfileFromGroup(profileId, groupId) {
    try {
      const response = await fetch(`${config.apiUrl}/radio-profiles/${profileId}/unassign-group`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify({ group_id: groupId })
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error unassigning radio profile from group:', error);
      throw error;
    }
  },

  async assignRadioProfileToDevice(profileId, serial, { treeId } = {}) {
    try {
      const body = { serial };
      if (treeId) body.tree_id = treeId;
      const response = await fetch(`${config.apiUrl}/radio-profiles/${profileId}/assign-device`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify(body)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error assigning radio profile to device:', error);
      throw error;
    }
  },

  async unassignRadioProfileFromDevice(profileId, serial) {
    try {
      const response = await fetch(`${config.apiUrl}/radio-profiles/${profileId}/unassign-device`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify({ serial })
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error unassigning radio profile from device:', error);
      throw error;
    }
  },

  // Bulk Radio Profile assignment — one BE call for N serials/group_ids.
  // See assignSSIDToDevices for the AbortSignal contract.
  async assignRadioProfileToDevices(profileId, serials, { signal, deviceTreeIds } = {}) {
    try {
      const body = { serials };
      if (deviceTreeIds && Object.keys(deviceTreeIds).length > 0) {
        body.device_tree_ids = deviceTreeIds;
      }
      const response = await fetch(`${config.apiUrl}/radio-profiles/${profileId}/assign-devices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify(body),
        signal
      });
      return await handleResponse(response);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Error bulk-assigning radio profile to devices:', error);
      }
      throw error;
    }
  },

  async unassignRadioProfileFromDevices(profileId, serials, { signal } = {}) {
    try {
      const response = await fetch(`${config.apiUrl}/radio-profiles/${profileId}/unassign-devices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify({ serials }),
        signal
      });
      return await handleResponse(response);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Error bulk-unassigning radio profile from devices:', error);
      }
      throw error;
    }
  },

  async assignRadioProfileToGroups(profileId, groupIds, { signal } = {}) {
    try {
      const response = await fetch(`${config.apiUrl}/radio-profiles/${profileId}/assign-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify({ group_ids: groupIds }),
        signal
      });
      return await handleResponse(response);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Error bulk-assigning radio profile to groups:', error);
      }
      throw error;
    }
  },

  async unassignRadioProfileFromGroups(profileId, groupIds, { signal } = {}) {
    try {
      const response = await fetch(`${config.apiUrl}/radio-profiles/${profileId}/unassign-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.getToken()}` },
        body: JSON.stringify({ group_ids: groupIds }),
        signal
      });
      return await handleResponse(response);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Error bulk-unassigning radio profile from groups:', error);
      }
      throw error;
    }
  },

};
